import { createFileRoute, Link } from "@tanstack/react-router";
import { Train, MapPin, ShoppingBag } from "lucide-react";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { pageHeadForProvince, getActiveSiteUrl } from "@/lib/seo-helpers";
import { buildBreadcrumbSchema, buildSpeakableSchema } from "@/lib/schema";

type NearKind = "bts" | "mrt" | "arl" | "mall";
type NearItem = {
  slug: string;
  nameTh: string;
  nameEn: string;
  line?: string;
  nearby: string[];
};
type NearLoaderData = {
  kindLabel: Record<NearKind, string>;
  groups: Record<NearKind, NearItem[]>;
};
const NEAR_KINDS: NearKind[] = ["bts", "mrt", "arl", "mall"];

export const Route = createFileRoute("/near/")({
  loader: async (): Promise<NearLoaderData> => {
    const { NEAR_PLACES, NEAR_PLACE_KIND_LABEL } = await import("@/content/near/places");
    const project = (kind: NearKind): NearItem[] =>
      NEAR_PLACES.filter((p) => p.kind === kind).map((p) => ({
        slug: p.slug,
        nameTh: p.nameTh,
        nameEn: p.nameEn,
        line: p.line,
        nearby: p.nearby.slice(0, 2),
      }));
    return {
      kindLabel: { ...NEAR_PLACE_KIND_LABEL } as Record<NearKind, string>,
      groups: Object.fromEntries(NEAR_KINDS.map((k) => [k, project(k)])) as Record<
        NearKind,
        NearItem[]
      >,
    };
  },
  head: () => {
    const site = getActiveSiteUrl();
    const base = pageHeadForProvince({
      title: "Notary ใกล้ BTS/MRT และห้างกรุงเทพ — On-site ภายในวันเดียวกัน",
      description:
        "บริการ Notary Public, แปลรับรอง, MFA และวีซ่า on-site ใกล้สถานี BTS/MRT/ARL และห้างใหญ่ในกรุงเทพ — ทนายเดินทางถึงจุดนัดภายในวันเดียวกัน",
      path: "/near",
    });
    return {
      ...base,
      scripts: [
        { type: "application/ld+json", children: JSON.stringify(buildSpeakableSchema("/near")) },
        {
          type: "application/ld+json",
          children: JSON.stringify(
            buildBreadcrumbSchema([
              { name: "หน้าแรก", url: site },
              { name: "Near me — BTS/MRT & ห้าง", url: `${site}/near` },
            ]),
          ),
        },
      ],
    };
  },
  component: NearIndex,
});

function NearIndex() {
  const { groups, kindLabel } = Route.useLoaderData() as NearLoaderData;
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SiteHeader />
      <main id="main-content">
        <section className="border-b border-border bg-gradient-to-b from-primary/5 to-background">
          <div className="mx-auto max-w-5xl px-4 py-12">
            <div className="text-xs font-bold uppercase tracking-widest text-gold">
              Near me · On-site Bangkok
            </div>
            <h1 className="mt-2 text-3xl font-extrabold md:text-4xl">
              Notary, แปลรับรอง, MFA & วีซ่า — ใกล้ BTS/MRT และห้างใหญ่
            </h1>
            <p className="mt-4 max-w-3xl text-base leading-relaxed text-muted-foreground">
              เลือกสถานีรถไฟฟ้าหรือห้างที่ใกล้คุณ — ทีม Thai Notary จัดทนายและทีมแปลเดินทาง on-site ถึงคอนโด/สำนักงาน/โรงแรม/คาเฟ่ใกล้จุดนัดภายในวันเดียวกัน รับเคสรวมเสาร์–อาทิตย์
            </p>
          </div>
        </section>

        {NEAR_KINDS.map((k) => (
          <section key={k} className="border-b border-border">
            <div className="mx-auto max-w-5xl px-4 py-10">
              <h2 className="flex items-center gap-2 text-2xl font-bold">
                {k === "mall" ? <ShoppingBag className="h-5 w-5 text-gold" /> : <Train className="h-5 w-5 text-gold" />}
                {kindLabel[k]} ({groups[k].length})
              </h2>
              <div className="mt-5 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
                {groups[k].map((p) => (
                  <Link
                    key={p.slug}
                    to="/near/$place"
                    params={{ place: p.slug }}
                    className="group rounded-2xl border border-border bg-card p-4 transition-all hover:-translate-y-0.5 hover:border-gold"
                  >
                    <div className="text-sm font-bold text-primary group-hover:text-gold">{p.nameTh}</div>
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      {p.nameEn}
                      {p.line ? ` · ${p.line}` : ""}
                    </div>
                    <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                      <MapPin className="mr-1 inline h-3 w-3 text-gold" />
                      {p.nearby.join(", ")}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        ))}
      </main>
      <SiteFooter />
    </div>
  );
}
